import { GridColDef } from '@mui/x-data-grid-premium';
import { useMgPrisConfig } from '@retail/app/stores/app-config';
import { CustomDataGrid } from '@retail/components';
import { useRowColorGrouping } from '@retail/hooks';
import { AssortmentTableActions } from '@retail/my-assortment/components';
import { useFetchCompetitorPrices } from '@retail/my-assortment/data-access';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';
import { CompetitorItem, CompetitorPrices, Store } from '@retail/my-assortment/types';
import { baseAssortmentFilters, BaseAssortmentFilters } from '@retail/my-assortment/utils';
import {
  ExternalItemSource,
  PriceCompareCell,
  RetailerColumnHeader,
  useMgItemAndExternalNumberColumn,
} from '@retail/products/components';
import { useFilters } from '@retail/products/hooks';
import { useCallback, useMemo } from 'react';
const baseColumnProps: Partial<GridColDef> = {
  width: 180,
  headerName: '',
  headerAlign: 'left',
  align: 'left',
};
interface Props {
  assortmentId: number;
  externalItemSource: ExternalItemSource;
  viewCalculusCard?: (mgSupplierItemId: number) => void;
  viewProductCard: (mgSupplierItemId: number) => void;
  getSupplierIdForCalculus?: (mgSupplierItemId: number) => number | undefined;
}

export function AssortmentCompetitorView({
  assortmentId,
  externalItemSource,
  viewCalculusCard,
  viewProductCard,
  getSupplierIdForCalculus,
}: Props) {
  const t = useMyAssortmentTFunction();
  const { retailerLogoBaseUrl } = useMgPrisConfig();
  const { getRowColorForStripedTable } = useRowColorGrouping();

  const { filters } = useFilters<BaseAssortmentFilters>(baseAssortmentFilters);
  const { data: competitorPricesDTO, isLoading } = useFetchCompetitorPrices({
    assortmentId,
    supplierNumber: filters.supplierNumber ? Number(filters.supplierNumber) : undefined,
    productGroupNumbers: filters.productGroupNumbers,
    discountGroupCodes: filters.discountGroupCodes,
    query: filters.searchQuery ?? undefined,
    withAgreementPrices:
      filters.purchasePrice === 'UNSPECIFIED' ? undefined : filters.purchasePrice === 'YES',
  });

  const competitorPrices = useMemo(
    () => (competitorPricesDTO ? CompetitorPrices.fromDto(competitorPricesDTO) : undefined),
    [competitorPricesDTO]
  );

  const mgItemAndExternalNumberColumn = useMgItemAndExternalNumberColumn<CompetitorItem>({
    externalItemSource,
    columnProps: baseColumnProps,
  });

  const columns: GridColDef<CompetitorItem>[] = useMemo(() => {
    const baseColumns = [
      mgItemAndExternalNumberColumn,
      {
        ...baseColumnProps,
        width: 180,
        field: 'primaryText',
        headerName: t('myAssortment.columns.itemText'),
      },
    ];

    const competitorColumns: GridColDef<CompetitorItem>[] = (competitorPrices?.stores ?? []).map(
      (store) => ({
        ...baseColumnProps,
        field: store.storeNumber.toString(),

        renderHeader: () => (
          <RetailerColumnHeader
            retailerName={store.retailerName}
            storeName={store.name}
            retailerUrlName={store.urlName}
            logoUrl={Store.getCircularLogoUrl(store.urlName, retailerLogoBaseUrl)}
          />
        ),
        align: 'center' as const,
        headerAlign: 'center' as const,
        renderCell: ({ row }) => {
          const currentStore = row.storePrices.find(
            (storePrice) => storePrice.storeNumber === store.storeNumber
          );
          return currentStore ? (
            <PriceCompareCell
              currentPrice={{
                unit: currentStore.unit ?? '-',
                price: currentStore.price,
                priceDate: currentStore.lastObserved,
              }}
              prevPrice={
                currentStore.previousPrice
                  ? {
                      unit: currentStore.unit ?? '-',
                      price: currentStore.previousPrice,
                      priceDate: currentStore.previousLastObserved,
                    }
                  : undefined
              }
              url={currentStore.url}
              equivalentItem={currentStore.equivalentMgItemNumber}
              ownPrice={currentStore.ownPrice}
              ownPriceUnit={currentStore.ownUnit}
            />
          ) : (
            '-'
          );
        },
        valueGetter: (_: any, row) =>
          row.storePrices.find((storePrice) => storePrice.storeNumber === store.storeNumber)
            ?.price ?? 0,
      })
    );
    return [
      ...baseColumns,
      ...competitorColumns,
      {
        ...baseColumnProps,
        field: 'actions',
        width: 115,
        sortable: false,
        hideable: false,
        headerAlign: 'center',
        align: 'center',
        renderCell: ({ row }) => (
          <AssortmentTableActions
            supplierIdForCalculus={getSupplierIdForCalculus?.(row.mgSupplierItemId)}
            viewItemCalculusCard={
              viewCalculusCard ? () => viewCalculusCard(row.mgSupplierItemId) : undefined
            }
            viewItemProductCard={() => viewProductCard(row.mgSupplierItemId)}
          />
        ),
      },
    ];
  }, [
    competitorPrices?.stores,
    getSupplierIdForCalculus,
    mgItemAndExternalNumberColumn,
    retailerLogoBaseUrl,
    t,
    viewCalculusCard,
    viewProductCard,
  ]);

  const competitorColumnOrder = useMemo(() => {
    const state = localStorage.getItem('columnOrder');
    return state ? JSON.parse(state) : undefined;
  }, []);

  const saveReorderingOfColumns = useCallback((columnOrder: string[]) => {
    localStorage.setItem('columnOrder', JSON.stringify(columnOrder));
  }, []);

  const sortedColumns = useMemo(() => {
    return competitorColumnOrder
      ? columns.sort(
          (a, b) => competitorColumnOrder.indexOf(a.field) - competitorColumnOrder.indexOf(b.field)
        )
      : columns;
  }, [columns, competitorColumnOrder]);

  return (
    <CustomDataGrid<CompetitorItem>
      rows={competitorPrices?.competitorItems ?? []}
      columns={sortedColumns}
      rowHeight={70}
      getRowId={(row) => row.mgSupplierItemId}
      getRowClassName={getRowColorForStripedTable}
      onStateChange={(event) => saveReorderingOfColumns(event.columns.orderedFields)}
      hideFooter
      disableColumnMenu
      disableColumnReorder={false}
      initialState={{
        pinnedColumns: { left: ['mgItemAndExternalNumber', 'primaryText'], right: ['actions'] },
      }}
      loading={isLoading}
      sx={{
        '& .MuiDataGrid-columnHeaders': { marginLeft: 0 },
        '& .MuiDataGrid-row': { paddingLeft: 0 },
        '& .MuiDataGrid-topContainer': { paddingLeft: 0, paddingRight: 0 },
      }}
    />
  );
}
